import { useCallback, useEffect, useRef } from "react";

/**
 * A custom hook that manages an interval timer
 * @param {Function} callback - The function to call on each interval
 * @param {number} delay - The delay in milliseconds between calls
 * @param {boolean} immediate - Whether to call the callback immediately (default: true)
 * @returns {Object} - Object with start, stop, and restart methods
 */
const useInterval = (callback, delay, immediate = true) => {
  const intervalRef = useRef(null);
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (immediate) {
      callbackRef.current();
    }

    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, delay);
  }, [delay, immediate]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const restart = useCallback(() => {
    stop();
    start();
  }, [start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { start, stop, restart };
};

export default useInterval;
